import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  UseViewManagementParams,
  ViewOperations,
  ViewForPage,
  RowIdForPage,
} from './types';
import { PageType } from '../../types/generatedTypes';
import { useViewState } from '../../store/viewStore';
import {
  createGridStateUtils,
  createViewFromCurrentState,
  applyViewToGrid,
  compareViewWithCurrentState,
  resetGridToDefault,
  generateViewId,
  filterDataBySelectedRows,
} from './viewUtils';
import { showSnackbar } from '../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../context/Snackbar/Types';

export const useViewManagement = <T extends PageType>(
  params: UseViewManagementParams<RowIdForPage<T>>
): ViewOperations<ViewForPage<T>> => {
  const {
    pageType,
    apiRef,
    searchTerm,
    selectedRows,
    autosizeColumns,
    displayData,
    handleSearch,
    setSelectedRows,
    setAutosizeColumns,
    loadColumnVisibilityPreferences,
    isGroupingEnabled = false,
    setIsGroupingEnabled,
    groups = [],
    rowIdKey = 'id',
  } = params;

  // Get view state from Zustand store
  const viewState = useViewState(pageType);

  // Local UI state
  const [contextMenuAnchor, setContextMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [selectedViewId, setSelectedViewId] = useState<string>('');
  const [isSaveViewDialogOpen, setIsSaveViewDialogOpen] = useState(false);
  const [editingView, setEditingView] = useState<ViewForPage<T> | null>(null);
  const [editMode, setEditMode] = useState<'rename' | 'update'>('update');
  const [isSavedViewsPanelOpen, setIsSavedViewsPanelOpen] = useState(false);

  // Refs for tracking state
  const hasAttemptedRestore = useRef(false);
  const isRestoringView = useRef(false);
  const isInitialMount = useRef(true);
  const gridUtils = useRef(createGridStateUtils(apiRef, isGroupingEnabled));

  // Update grid utils when apiRef or grouping changes
  useEffect(() => {
    gridUtils.current = createGridStateUtils(apiRef, isGroupingEnabled);
  }, [apiRef, isGroupingEnabled]);

  // Load views on mount (only once)
  useEffect(() => {
    viewState.loadViews();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track view modifications when state changes (debounced to avoid infinite loops)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Skip modification tracking during initial mount or while restoring view
      if (isInitialMount.current || isRestoringView.current) {
        return;
      }

      if (viewState.activeView) {
        const modifications = compareViewWithCurrentState(
          viewState.activeView,
          {
            searchTerm,
            selectedRows,
            autosizeColumns,
            displayData,
            isGroupingEnabled,
          },
          gridUtils.current,
          {
            excludeRowSelection: true,
          }
        );
        viewState.setViewModifications(modifications);
      } else {
        viewState.setViewModifications([]);
      }
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [
    viewState.activeView,
    searchTerm,
    selectedRows,
    autosizeColumns,
    isGroupingEnabled,
    // Remove displayData from dependencies to avoid infinite loops
    // viewState.setViewModifications - also removing this as it might change frequently
    displayData,
    viewState,
  ]);

  // Apply persisted active view when data is available (only once)
  useEffect(() => {
    if (hasAttemptedRestore.current || displayData.length === 0) {
      return;
    }

    // Check if we have a persisted active view from Zustand persist
    if (viewState.activeView) {
      hasAttemptedRestore.current = true;

      // For views with groups, we need extra time for the grid to stabilize
      const hasGroups = viewState.activeView.isGroupingEnabled;
      const restoreDelay = hasGroups ? 150 : 50;

      setTimeout(() => {
        applyView(viewState.activeView);
      }, restoreDelay);
    }
    hasAttemptedRestore.current = true;

    // Clear initial mount flag after a timeout in case no view was restored
    const timeout = setTimeout(() => {
      isInitialMount.current = false;
    }, 2500);

    return () => clearTimeout(timeout);
  }, [displayData.length, pageType]);

  // Core view operations
  const applyView = useCallback(
    async (view: ViewForPage<T>) => {
      try {
        isRestoringView.current = true;
        viewState.setViewModifications([]);
        viewState.setActiveView(view);

        await applyViewToGrid(
          view,
          gridUtils.current,
          handleSearch,
          setSelectedRows,
          setAutosizeColumns,
          setIsGroupingEnabled
        );
      } catch (error) {
        console.error('Error in applyView:', error);
        // Error handling is done in applyViewToGrid
      } finally {
        // For views with groups, give extra time for the grid to fully stabilize
        const hasGroups = view.isGroupingEnabled;
        const stabilizationDelay = hasGroups ? 800 : 500;

        // Clear initial mount flag after a delay to ensure view is fully applied
        setTimeout(() => {
          isInitialMount.current = false;
          isRestoringView.current = false;
        }, stabilizationDelay);
      }
    },
    [
      handleSearch,
      setSelectedRows,
      setAutosizeColumns,
      setIsGroupingEnabled,
      viewState,
    ]
  );

  const saveView = useCallback(
    (viewName: string) => {
      const baseView = {
        id: editingView?.id || generateViewId(),
        name: viewName,
        searchTerm,
        autosizeColumns,
        selectedRows: selectedRows.length > 0 ? selectedRows : undefined,
        isGroupingEnabled,
      };

      const newView = createViewFromCurrentState(
        baseView,
        gridUtils.current
      ) as ViewForPage<T>;

      if (editingView) {
        // Update existing view
        viewState.updateViewById(editingView.id, newView);
        if (viewState.activeView?.id === editingView.id) {
          viewState.setActiveView(newView);
          viewState.setViewModifications([]);
        }
      } else {
        // Create new view
        viewState.addView(newView);
        viewState.setActiveView(newView);
        viewState.setViewModifications([]);
      }

      setIsSaveViewDialogOpen(false);
      setEditingView(null);

      showSnackbar({
        title: 'Success',
        message: `View "${viewName}" ${editingView ? 'updated' : 'saved'} successfully`,
        type: SnackbarType.success,
      });
    },
    [
      editingView,
      searchTerm,
      autosizeColumns,
      selectedRows,
      isGroupingEnabled,
      pageType,
      viewState.updateViewById,
      viewState.addView,
      viewState.setActiveView,
      viewState.setViewModifications,
      viewState.activeView?.id,
      viewState,
    ]
  );

  const deleteView = useCallback(
    (viewId: string) => {
      viewState.deleteViewById(viewId);
    },
    [viewState]
  );

  const renameView = useCallback((view: ViewForPage<T>) => {
    setEditingView(view);
    setEditMode('rename');
    setIsSaveViewDialogOpen(true);
  }, []);

  const updateView = useCallback((view: ViewForPage<T>) => {
    setEditingView(view);
    setEditMode('update');
    setIsSaveViewDialogOpen(true);
  }, []);

  const updateCurrentView = useCallback(() => {
    if (viewState.activeView) {
      const baseView = {
        ...viewState.activeView,
        searchTerm,
        autosizeColumns,
        selectedRows: selectedRows.length > 0 ? selectedRows : undefined,
        isGroupingEnabled,
      };

      const updatedView = createViewFromCurrentState(
        baseView,
        gridUtils.current
      ) as ViewForPage<T>;

      viewState.updateViewById(viewState.activeView.id, updatedView);
      viewState.setActiveView(updatedView);
      viewState.setViewModifications([]);

      showSnackbar({
        title: 'Success',
        message: `View "${viewState.activeView.name}" updated with current state`,
        type: SnackbarType.success,
      });
    }
  }, [
    searchTerm,
    autosizeColumns,
    selectedRows,
    isGroupingEnabled,
    pageType,
    viewState.updateViewById,
    viewState.setActiveView,
    viewState.setViewModifications,
    viewState,
  ]);

  const clearActiveView = useCallback(() => {
    viewState.setActiveView(null);
    viewState.setViewModifications([]);

    resetGridToDefault(
      gridUtils.current,
      apiRef,
      handleSearch,
      setSelectedRows,
      setAutosizeColumns,
      loadColumnVisibilityPreferences,
      setIsGroupingEnabled
    );
  }, [
    viewState,
    apiRef,
    handleSearch,
    setSelectedRows,
    setAutosizeColumns,
    loadColumnVisibilityPreferences,
    setIsGroupingEnabled,
  ]);

  // Dialog handlers
  const openSaveDialog = useCallback(() => {
    setEditingView(null);
    setEditMode('update');
    setIsSaveViewDialogOpen(true);
  }, []);

  const openSavedViewsPanel = useCallback(() => {
    setIsSavedViewsPanelOpen(true);
  }, []);

  const closeSaveDialog = useCallback(() => {
    setIsSaveViewDialogOpen(false);
    setEditingView(null);
  }, []);

  const closeSavedViewsPanel = useCallback(() => {
    setIsSavedViewsPanelOpen(false);
    setContextMenuAnchor(null);
  }, []);

  // View selection handlers
  const handleViewClick = useCallback(
    (view: ViewForPage<T>) => {
      // Don't re-apply if this view is already active
      if (viewState.activeView?.id === view.id) {
        setIsSavedViewsPanelOpen(false);
        return;
      }

      applyView(view);
      setIsSavedViewsPanelOpen(false);
    },
    [applyView, viewState.activeView?.id]
  );

  const handleMenuClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, viewId: string) => {
      setContextMenuAnchor(event.currentTarget);
      setSelectedViewId(viewId);
    },
    []
  );

  // Context menu handlers
  const handleApplyView = useCallback(() => {
    const view = viewState.savedViews.find(v => v.id === selectedViewId);
    if (view) applyView(view as ViewForPage<T>);
    setContextMenuAnchor(null);
    setIsSavedViewsPanelOpen(false);
  }, [viewState.savedViews, selectedViewId, applyView]);

  const handleRenameView = useCallback(() => {
    const view = viewState.savedViews.find(v => v.id === selectedViewId);
    if (view) renameView(view as ViewForPage<T>);
    setContextMenuAnchor(null);
  }, [viewState.savedViews, selectedViewId, renameView]);

  const handleUpdateView = useCallback(() => {
    const view = viewState.savedViews.find(v => v.id === selectedViewId);
    if (view) updateView(view as ViewForPage<T>);
    setContextMenuAnchor(null);
  }, [viewState.savedViews, selectedViewId, updateView]);

  const handleDeleteView = useCallback(() => {
    if (selectedViewId) deleteView(selectedViewId);
    setContextMenuAnchor(null);
  }, [selectedViewId, deleteView]);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenuAnchor(null);
  }, []);

  // Comparison function
  const compareWithAppliedView = useCallback((): string[] => {
    return compareViewWithCurrentState(
      viewState.activeView,
      {
        searchTerm,
        selectedRows,
        autosizeColumns,
        displayData,
        isGroupingEnabled,
      },
      gridUtils.current,
      {
        excludeRowSelection: true,
      }
    );
  }, [
    viewState.activeView,
    searchTerm,
    selectedRows,
    autosizeColumns,
    displayData,
    isGroupingEnabled,
  ]);

  // Filter display data based on selected rows in active view
  const filteredDisplayData = useMemo(
    () => filterDataBySelectedRows(displayData, viewState.activeView, rowIdKey),
    [displayData, viewState.activeView, rowIdKey]
  );

  return {
    // State from Zustand store
    savedViews: viewState.savedViews,
    activeView: viewState.activeView,
    viewModifications: viewState.viewModifications,
    setViewModifications: viewState.setViewModifications,
    filteredDisplayData,

    // Local UI state
    contextMenuAnchor,
    selectedViewId,
    isSaveViewDialogOpen,
    editingView,
    editMode,
    isSavedViewsPanelOpen,

    // Core operations
    applyView,
    saveView: saveView,
    deleteView,
    renameView,
    updateView,
    updateCurrentView,
    clearActiveView,

    // Dialog handlers
    openSaveDialog,
    openSavedViewsPanel,
    closeSaveDialog,
    closeSavedViewsPanel,

    // View selection
    handleViewClick,
    handleMenuClick,

    // Context menu handlers
    handleApplyView,
    handleRenameView,
    handleUpdateView,
    handleDeleteView,
    handleCloseContextMenu,

    // Grid state functions (exposed for compatibility)
    getColumnFilters: gridUtils.current.getColumnFilters,
    getSortModel: gridUtils.current.getSortModel,
    getColumnVisibility: gridUtils.current.getColumnVisibility,
    getPinnedColumns: gridUtils.current.getPinnedColumns,

    // Comparison
    compareWithAppliedView,
  };
};
